import { Menu, Dropdown, Spin, Modal, Popover } from 'antd';
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { MenuOutlined } from '@ant-design/icons';
import PortfolioRuleEngine from "../../RuleEngine";
import { usePortfolio } from "../../../../Contexts/PortfolioContext";
import { useBuilder } from "../../../../Contexts/BuilderContext";
import { fetchDetailedPortfolioObjects } from "../../../../Api/portfolioApi";
import useClipboard from "../../../../hooks/useClipboard";
import {useGptResponses} from "../../../../hooks/useGptResponses";
import ResponseMessage from "../../../../SuperComponents/Gpt/ResponseMessage";
import {AppstoreOutlined} from "@ant-design/icons";
import {
    OpenInNewRounded, ImportContactsRounded, FileCopyRounded, EditRounded, TuneRounded, HistoryRounded,
    CommentRounded, AddCircleRounded, DashboardCustomizeRounded
} from "@mui/icons-material";


const extractSectors = (sectorsObj)=>{
    const sectorsArray = Object.keys(sectorsObj).map((key)=>{return {name: key, value: sectorsObj[key]}});
    sectorsArray.sort((a, b)=>b.value-a.value);
    return sectorsArray;
}

const extractPortfolioDataForPrompt = (portfolio) => {
    const sectors = extractSectors(portfolio.tags_v2?.sector_gic);
    let promptData = 'Portfolio Data: \n';
    promptData += `Div Yield: ${portfolio?.tags_v2?.metrics.dy}; PE (TTM) ${portfolio.tags_v2?.metrics.pe_ttm}`;
    promptData += `\nSectors: ${sectors.map(s=>`${s.name} (${s.value})`).join(', ')}`;
    return promptData
}


const PROMPT_HEAD = `Describe the portfolio based on the data provided. Highlight how the portfolio is positioned for 
modern market conditions. Ensure no negative remarks or insights are made on the portfolio, and really bridge how the
current positioning is beneficial based on current market and geopolitical environment`;


/**
 *
 * @param onEdit {function} - Function to handle the edit action
 * @param portfolio {object} - Portfolio object document
 * @returns {JSX.Element}
 * @constructor
 */
const TableRowActions = ({ onEdit, portfolio }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userId = localStorage.getItem('uid');
    const { setExistingPortfolioBuilderContext } = useBuilder();
    const { selectSmaPortfolio } = usePortfolio(); // Use the new setter for SMA Portfolio
    const [currentAction, setCurrentAction] = useState(null);
    const [fetchComplete, setFetchComplete] = useState(false);
    const [fetchedPortfolioId, setFetchedPortfolioId] = useState(null);
    const [loadingResponse, setLoadingResponse] = useState(false);
    const [showGptSummary, setShowGptSummary] = useState(false);
    const copyToClipboard = useClipboard();
    const { responseId, submitPrompt } = useGptResponses();
    const gptResponse = useSelector(state => state.gptApi.responses[responseId]);


    const updatedPortfolio = useSelector(state =>
        state.portfolios.items.find(p => p.id === fetchedPortfolioId)
    );
    const portfolioUserId = portfolio?.attr?.create_uid;

    const handleSmaAfterFetch = ()=>{
        let smaConfig = {symbols: updatedPortfolio.symbols, params: {}, portfolio: {}, linkedPortfolio: portfolio}
            if (fetchedPortfolioId !== portfolio.id) {
                smaConfig.params = portfolio.properties.sma_config;
                smaConfig.portfolio = portfolio;
                smaConfig.linkedPortfolio = updatedPortfolio;
            }
            if (updatedPortfolio && updatedPortfolio.symbols) {
                selectSmaPortfolio(smaConfig);
                navigate('/builders/sma');
            }
    }
    const handleUpdatePositionsAfterFetch = ()=>{
        setExistingPortfolioBuilderContext(updatedPortfolio);
        navigate('/builders/portfolio');
    }




    useEffect(() => {
        if (fetchComplete) {
            setFetchComplete(false); // Reset the fetchComplete flag
            if (currentAction==='sma'){
                handleSmaAfterFetch();
            }
            else if (currentAction==='positions') {
                handleUpdatePositionsAfterFetch();
            }
            else if (currentAction === 'explain') {
                const promptString = extractPortfolioDataForPrompt(updatedPortfolio);
                submitPrompt(`${PROMPT_HEAD}\r\n${promptString}`, `Explain: ${updatedPortfolio.name}`);
                setShowGptSummary(true);

                // handlePortfolioPrompt(updatedPortfolio);
            }
        }
    }, [fetchComplete, updatedPortfolio, navigate, selectSmaPortfolio]);

    const _fetchAndHandlePortfolioAction = (portfolioId) => {
        dispatch(fetchDetailedPortfolioObjects([portfolioId])).then(() => {
            setFetchComplete(true);
        });
    };

    const handleSMA = (pid)=>{
        setCurrentAction('sma');
        setFetchedPortfolioId(pid);
        _fetchAndHandlePortfolioAction(pid);
    }


    const updatePortfolioPositions = ()=>{
        setCurrentAction('positions');
        setFetchedPortfolioId(portfolio.id)
        _fetchAndHandlePortfolioAction(portfolio.id, (payload)=>{
            setExistingPortfolioBuilderContext(payload);
            navigate('/builders/portfolio');
        })
    }

    const handleExplainPortfolio = (portfolioId) => {
      setCurrentAction('explain');
      setFetchedPortfolioId(portfolioId);
      _fetchAndHandlePortfolioAction(portfolioId);
    };

    let rules = new PortfolioRuleEngine(portfolio);
    let isSMA = rules.isSMA();

    let menuItems = [
        {
            key: 'open',
            label: 'Open in New Tab',
            onClick: ()=>window.open(`/public/portfolio/${portfolio.id}`),
            icon: <OpenInNewRounded/>,
            bg: '#66A6FF',
            color: "#fff"
        },
        {
            key: 'view',
            label: 'View in Page',
            onClick: ()=>navigate(`/portfolio/${portfolio.id}`),
            icon: <ImportContactsRounded/>,
            bg: '#BADDFE'

        },
        {
            key: 'copy',
            label: "Copy ID",
            onClick: ()=>copyToClipboard(portfolio.id),
            icon: <FileCopyRounded/>,
            bg: '#6D869B',
            color: "#fff"
        },
        {
            key: 'positions',
            label: 'Signal History',
            onClick: ()=>navigate(`/signals/${portfolio.id}/history`),
            icon: <HistoryRounded/>,
            bg: '#BADDFE'

        },
        {
            key: 'sma',
            label: isSMA ? 'Edit SMA' : 'Create SMA',
            onClick: ()=>handleSMA(isSMA?portfolio.properties.linked_portfolio.id:portfolio.id),
            icon: <AddCircleRounded/>,
            bg: '#66A6FF',
            color: "#fff"
        },
        {
          key: 'explain',
          label: 'Explain Portfolio',
          onClick: () => handleExplainPortfolio(portfolio.id),
          icon: <CommentRounded/>,
          bg: '#6D869B',
          color: "#fff"
        },
        {
            key: 'factSheet',
            label: 'Fact Sheet',
            onClick: ()=>navigate(`/portfolio/${portfolio.id}/factsheet`),
            bg: 'linear-gradient( 69deg, rgba(0, 145, 255, .06) 0%, rgba(96, 73, 231, .06) 33%, rgba(234, 97, 230, .06) 66%, rgba(233, 61, 130, .06) 100% )',
            className: 'menu-banner-title',
            icon: <DashboardCustomizeRounded style={{color: '#5a43d6'}}/>,
        },
        {
            key: 'incomeModel',
            label: 'Income Model',
            onClick: ()=>navigate(`/models/income/${portfolio.id}`),
            bg: 'linear-gradient( 69deg, rgba(0, 145, 255, .06) 0%, rgba(96, 73, 231, .06) 33%, rgba(234, 97, 230, .06) 66%, rgba(233, 61, 130, .06) 100% )',
            className: 'menu-banner-title',
            icon: <DashboardCustomizeRounded style={{color: '#5a43d6'}}/>,
        }
    ];

    if (portfolioUserId && portfolioUserId===userId){
        menuItems.unshift(...[
            {
                key: 'updatePositions',
                label: 'Update Positions',
                onClick: updatePortfolioPositions,
                icon: <TuneRounded/>,
                bg: '#66A6FF',
                color: "#fff"
            },
            {
                key: "edits",
                label: "Edit",
                onClick: onEdit,
                icon: <EditRounded/>,
                bg: '#6D869B',
                color: "#fff"
            },
        ])
    }

    const ItemIcon = ({icon})=>{
        return icon ? icon: <AppstoreOutlined style={{ fontSize: '18px' }} />
    }

    const popoverContent = (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0px', padding: '0' }}>
            {menuItems.map(item => (
                <div class={`menu-hover cursor ${item?.className??''}`} key={item.key} style={{
                    textAlign: 'center',
                    padding: '10px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '0',
                    background: item?.bg ?? '#fff',
                    color: item?.color ?? '#000'
                }} onClick={item.onClick}>
                    <ItemIcon icon={item?.icon} />
                    <div class={'menu-popup-label'}>{item.label}</div>
                </div>
            ))}
        </div>
    );

    return (
        <>
            <Popover content={popoverContent} trigger="hover">
                <MenuOutlined style={{ fontSize: '18px' }} />
            </Popover>
            <Modal open={showGptSummary} onCancel={()=>setShowGptSummary(false)}>
                {loadingResponse && <div style={{padding: '40px'}}>
                    <Spin /> Loading Response
                </div>}
                {gptResponse?.data && <ResponseMessage response={gptResponse} />}
            </Modal>
        </>
    );
};

export default TableRowActions;
